import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate, useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';

import { userAPI, codeAPI } from '../../actions/imsServer';
import { useInterval } from '../../actions/interval';
import StyledContainer from './components/styledContainer';
import Navigation from '../navigation';
import InputField from '../formItem/inputField';
import Button from '../button';
import FormLabel from '../formItem/formLabel';
import { isEmail, isValidPassword } from '../../utils/password-validation';

export const ResetPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { type, email: locationEmail } = location.state || {};
    const [email, setEmail] = useState(locationEmail || '');
    const [code, setCode] = useState('');
    const [step, setStep] = useState(0);
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [hasSentCode, setHasSentCode] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // Check if user is grade 80 or above
    const meInfo = JSON.parse(localStorage.getItem('meInfo'));

    React.useEffect(() => {
        // Set initial step for high grade users
        if (meInfo?.grade >= 80) {
            setStep(1);
        }
    }, [meInfo]);

    // 카운트다운 interval (useInterval 사용)
    useInterval(() => {
        if (countdown > 0) {
            setCountdown(prev => prev - 1);
        }
    }, countdown > 0 ? 1000 : null);

    const handleClearEmail = () => setEmail('');
    const handleSendCode = async () => {
        // 버튼을 누르자마자 비활성화
        setCountdown(60);
        setHasSentCode(true);

        try {
            // type에 따라 다른 유효성 검사 적용
            let isValidEmail;

            // 이메일 검사
            isValidEmail = isEmail(email);
            if (!email || !isValidEmail) {
                setEmailError(true);
                setEmailErrorMessage(t('please_enter_a_valid_email_address'));
                // 유효성 검사 실패 시 버튼 상태 복원
                setCountdown(0);
                setHasSentCode(false);
                return;
            }

            let loginIdForCheck;
            let typeForCode;

            loginIdForCheck = email;
            typeForCode = 0; // 이메일 타입

            const checkResponse = await userAPI.checkUserExists({
                loginId: loginIdForCheck,
            });
            if (checkResponse.data.exists === false) {
                setSnackbar({
                    open: true,
                    message: t('does_not_exist'),
                    severity: 'error',
                });
                // 사용자 존재하지 않음 시 버튼 상태 복원
                setCountdown(0);
                setHasSentCode(false);
                return;
            }
            await codeAPI.generateCode({
                target: loginIdForCheck,
                type: typeForCode,
            });
            setSnackbar({
                open: true,
                message: `${t('verification_code_sent')} ${email}`,
                severity: 'success',
            });
            // 성공 시에는 이미 설정된 상태 유지
        } catch (error) {
            console.error('인증번호 발송 실패:', error);

            // 에러 발생 시 버튼 상태 복원
            setCountdown(0);
            setHasSentCode(false);

            // 에러 메시지가 'code_limit_exceeded_per_day'인 경우 특정 메시지 표시
            if (error?.message === 'code_limit_exceeded_per_day') {
                setSnackbar({
                    open: true,
                    message: t('the_number_of_attempts_has_been_exceeded_please_try_again_tomorrow'),
                    severity: 'error',
                });
            } else {
                setSnackbar({
                    open: true,
                    message: t('an_error_occurred'),
                    severity: 'error',
                });
            }
        }
    };

    const handleNext = async () => {
        setLoading(true);
        try {
            let targetForConfirm;
            let typeForConfirm;

            targetForConfirm = email;
            typeForConfirm = 0; // 이메일 타입

            const response = await codeAPI.confirmCode({
                target: targetForConfirm,
                type: typeForConfirm,
                key: code,
            });
            if (response.result === 'ok') {
                if (type === 'findPassword') {
                    let loginIdForFindPassword;

                    loginIdForFindPassword = email;

                    const findPasswordResponse = await userAPI.findPassword({
                        loginId: loginIdForFindPassword,
                    });
                    setCurrentPassword(findPasswordResponse.data.loginPwd);
                }
                setStep(1);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: `${t('invalid_verification_code')}`,
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClearCurrentPassword = () => setCurrentPassword('');
    const handleClearPassword = () => setPassword('');
    const handleClearPasswordConfirm = () => setPasswordConfirm('');
    const handleChangePassword = async () => {
        setLoading(true);
        try {
            let loginIdForChange;

            // 사용할 loginId 결정 (email이 있으면 email, 없으면 meInfo.loginId)
            const targetLoginId = email || meInfo.loginId;
            loginIdForChange = targetLoginId;

            await userAPI.changePassword({
                loginId: loginIdForChange,
                currentPassword,
                newPassword: password,
            });

            navigate(-1);
        } catch (err) {
            alert(t('failed_to_change_password') + ': ' + err?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEmailChange = event => {
        let value = event.target.value;
        setEmail(value);

        // 이메일 검사
        const isValidEmail = isEmail(value);
        if (!value || !isValidEmail) {
            setEmailError(true);
            setEmailErrorMessage(t('please_enter_a_valid_email_address'));
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }
    };

    const handleCodeChange = event => {
        const value = event.target.value;
        setCode(value);
    };

    return (
        <StyledContainer>
            {/* Navigation Header */}
            <Navigation title={t('reset_password')} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{
                    // AOS 웹뷰에서 상단에 안전하게 표시되도록 여백 조정
                    mt: { xs: '60px', sm: '20px' },
                }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar(prev => ({ ...prev, open: false }))
                    }
                    severity={snackbar.severity}
                    sx={{
                        width: '100%',
                        borderRadius: '8px',
                        // AOS 웹뷰에서 더 잘 보이도록 스타일 강화
                        fontSize: '14px',
                        fontWeight: 500,
                        boxShadow: theme.palette.shadows?.custom?.tooltip || '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {step === 0 ? (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '60px',
                            alignSelf: 'stretch',
                            width: '100%',
                            padding: '40px 0px 0px 0px',
                            flex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '20px',
                                width: '100%',
                            }}
                        >
                            <FormLabel text={t('please_verify_your_email_address')} />

                            {/* 이메일 입력 필드 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '10px',
                                    width: '100%',
                                }}
                            >
                                <FormLabel text={t('e_mail')} />
                                <InputField
                                    fullWidth
                                    placeholder={t('enter_your_email')}
                                    value={email}
                                    onChange={handleEmailChange}
                                    error={emailError}
                                    helperText={emailErrorMessage || ' '}
                                    showClearIcon={!!email}
                                    onClear={handleClearEmail}
                                />
                            </Box>
                            {/* 인증번호 입력 필드 */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '10px',
                                    width: '100%',
                                }}
                            >
                                <FormLabel text={t('verification_code')} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: '10px',
                                        width: '100%',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <InputField
                                        fullWidth
                                        placeholder={t(
                                            'verification_number'
                                        )}
                                        value={code}
                                        type="password"
                                        onChange={handleCodeChange}
                                        showClearIcon={false}
                                        showVisibilityIcon={true}
                                    />
                                    <Button
                                        variant="normal"
                                        size="small"
                                        onClick={handleSendCode}
                                        disabled={
                                            countdown > 0 ||
                                            emailError ||
                                            !email ||
                                            !isEmail(email)
                                        }
                                        sx={{
                                            minWidth: '120px',
                                            height: '40px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {countdown > 0
                                            ? `${t('resend')}(${countdown}s)`
                                            : hasSentCode
                                                ? t('resend_code')
                                                : t('send_code')}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '60px',
                        width: '100%',
                        padding: '40px 0px 0px 0px',
                        flex: 1,
                    }}
                >
                    {/* Content */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px',
                            width: '100%',
                        }}
                    >
                        {/* 계정 정보 표시 */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                            }}
                        >
                            <FormLabel text={t('e_mail')} />
                            <Typography
                                sx={{
                                    color: theme.palette.figma.white[100],
                                    textAlign: 'center',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: 'normal',
                                    letterSpacing: '-0.48px',
                                }}
                            >
                                {email || meInfo.loginId}
                            </Typography>
                        </Box>
                        {/* 비밀번호 입력 필드들 */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '0px',
                                width: '100%',
                            }}
                        >
                            <FormLabel text={t('password')} />

                            {type !== 'findPassword' && (
                                <InputField
                                    fullWidth
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={t('enter_your_password')}
                                    value={currentPassword}
                                    onChange={e =>
                                        setCurrentPassword(e.target.value)
                                    }
                                    showClearIcon={true}
                                    onClear={handleClearCurrentPassword}
                                />
                            )}

                            <InputField
                                fullWidth
                                type="password"
                                showVisibilityIcon={true}
                                placeholder={t('enter_your_password')}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                showClearIcon={true}
                                onClear={handleClearPassword}
                                helperText={' '}
                            />

                            <InputField
                                fullWidth
                                type="password"
                                showVisibilityIcon={true}
                                placeholder={t(
                                    'form_password_confirm_placeholder'
                                )}
                                value={passwordConfirm}
                                onChange={e =>
                                    setPasswordConfirm(e.target.value)
                                }
                                showClearIcon={true}
                                onClear={handleClearPasswordConfirm}
                                helperText={' '}
                            />

                            <Typography
                                sx={{
                                    color: theme.palette.figma.white[60],
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    lineHeight: 'normal',
                                    letterSpacing: '-0.36px',
                                    textAlign: 'center',
                                }}
                            >
                                {t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Bottom Button */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    width: '100%',
                    padding: '0 0 20px 0',
                }}
            >
                <Button
                    variant="normal"
                    size="large"
                    onClick={step === 0 ? handleNext : handleChangePassword}
                    disabled={
                        loading ||
                        (step === 0
                            ? !code
                            : !(
                                currentPassword &&
                                password &&
                                passwordConfirm &&
                                password === passwordConfirm &&
                                isValidPassword(password)
                            ))
                    }
                    sx={{ width: '100%' }}
                >
                    {loading
                        ? t('processing')
                        : step === 0
                            ? t('next')
                            : t('confirm')}
                </Button>
            </Box>
        </StyledContainer>
    );
};
